import React, { useEffect, useState } from 'react';
import {
  Badge, Box, ButtonBase, Collapse, Divider, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  hubForPath, visibleHubs, visibleItems, defaultPath, NavPerms, FALLBACK_MODULES, PRIMARY_HUB_IDS, Hub,
} from '../../config/navHubs';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import { useNotifications, NotificationCounts } from '../../contexts/NotificationContext';
import apiClient from '../../services/api/config';
import {
  Home, School, Spa, Assessment, EmojiEvents, Assignment, Forum, BeachAccess,
  MoreHoriz, Analytics, AdminPanelSettings, ExpandLess, ExpandMore,
} from '@mui/icons-material';

const MANAGER_ROLES = ['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin'];
const ADMIN_ROLES = ['admin', 'company-super-admin', 'super-super-admin'];

const BAR_HEIGHT = 58;

// Hub id → bottom-bar icon (mirrors NavigationRail's HUB_ICONS).
const HUB_ICONS: Record<string, React.ReactNode> = {
  home: <Home />, grow: <School />, wellbeing: <Spa />, performance: <Assessment />,
  rewards: <EmojiEvents />, workspace: <Assignment />, connect: <Forum />, leave: <BeachAccess />,
};

// Which unread counts roll up onto which hub.
const HUB_BADGES: Record<string, (keyof NotificationCounts)[]> = {
  grow: ['learning', 'games'],
  connect: ['announcements', 'pulse'],
  rewards: ['rewards', 'recognition'],
  workspace: ['tasks', 'documents', 'calendar', 'policies'],
};

const ADMIN_LINKS = [
  { label: 'Insights', path: '/insights', icon: <Analytics /> },
  { label: 'Admin Hub', path: '/admin', icon: <AdminPanelSettings /> },
];

/**
 * Persistent mobile bottom bar. The four hubs people use most sit in it
 * directly; the rest, and the admin links, open from "More" as a bottom
 * sheet. Eight equal slots used to leave 45px per hub on a 360px phone,
 * which truncated every label.
 *
 * While it is on screen it publishes its height as --app-bottom-inset so
 * floating buttons, banners and snackbars can sit above it.
 */
export default function BottomHubBar() {
  const { user } = useAuth();
  const { branding, mode } = useBranding();
  const { counts } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [enabledModules, setEnabledModules] = useState<string[]>(FALLBACK_MODULES);
  const [moreOpen, setMoreOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiClient.get('/company/modules')
      .then((res) => {
        if (!active) return;
        const mods = (res.data?.data || []).filter((m: { isActive: boolean }) => m.isActive).map((m: { moduleName: string }) => m.moduleName);
        if (mods.length) setEnabledModules(mods);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-bottom-inset', `calc(${BAR_HEIGHT}px + env(safe-area-inset-bottom))`);
    return () => { root.style.setProperty('--app-bottom-inset', '0px'); };
  }, []);

  // Close the sheet whenever the route changes underneath it.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const isManager = !!user && MANAGER_ROLES.includes(user.role);
  const isAdmin = !!user && ADMIN_ROLES.includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';
  const navPerms: NavPerms = { enabledModules, isManager, isAdmin };

  const hubs = visibleHubs(navPerms);
  const activeHub = hubForPath(location.pathname);

  // Everything fits when there are five or fewer destinations; otherwise the
  // primary hubs stay in the bar and the rest go behind More.
  const adminLinks = isAdmin
    ? ADMIN_LINKS.map((l) => (l.path === '/admin' && isSuperSuperAdmin ? { ...l, label: 'Super Admin', path: '/admin/super' } : l))
    : [];
  const needsMore = hubs.length > 5 || adminLinks.length > 0;
  const primary = needsMore
    ? PRIMARY_HUB_IDS.map((id) => hubs.find((h) => h.id === id)).filter((h): h is Hub => !!h)
    : hubs;
  const overflow = hubs.filter((h) => !primary.includes(h));

  const badgeFor = (hub: Hub) => (HUB_BADGES[hub.id] || []).reduce((sum, key) => sum + (counts[key] || 0), 0);
  const moreBadge = overflow.reduce((sum, h) => sum + badgeFor(h), 0);
  const moreActive = (!!activeHub && overflow.some((h) => h.id === activeHub.id))
    || adminLinks.some((l) => location.pathname === l.path || location.pathname.startsWith(l.path + '/'));

  const barBg = mode === 'dark' ? (branding.sidebarColor || '#1A1D1F') : '#FFFFFF';
  const primaryColor = branding.primaryColor || '#2D5BE3';
  const idle = mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.56)';

  const go = (path: string | null) => { if (path) navigate(path); };

  const slot = (key: string, label: string, icon: React.ReactNode, active: boolean, badge: number, onClick: () => void) => (
    <ButtonBase
      key={key}
      onClick={onClick}
      aria-label={badge > 0 ? `${label}, ${badge} new` : label}
      aria-current={active ? 'page' : undefined}
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        height: BAR_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        position: 'relative',
        color: active ? primaryColor : idle,
        transition: 'color 0.15s ease',
        '& svg': { fontSize: 24 },
        '&::before': active ? {
          content: '""',
          position: 'absolute',
          top: 0,
          width: 28,
          height: 3,
          borderRadius: '0 0 3px 3px',
          backgroundColor: primaryColor,
        } : {},
      }}
    >
      <Badge badgeContent={badge} max={99} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
        {icon}
      </Badge>
      <Typography
        component="span"
        noWrap
        sx={{ fontSize: 12, lineHeight: 1.1, fontWeight: active ? 700 : 500, maxWidth: '100%', px: '2px' }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );

  return (
    <>
      <Box
        component="nav"
        aria-label="Main"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: barBg,
          borderTop: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow: '0 -2px 12px rgba(0,0,0,0.12)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          alignItems: 'stretch',
        }}
      >
        {primary.map((hub) => slot(
          hub.id,
          hub.label,
          HUB_ICONS[hub.icon],
          activeHub?.id === hub.id,
          badgeFor(hub),
          () => go(defaultPath(hub, navPerms)),
        ))}
        {needsMore && slot('more', 'More', <MoreHoriz />, moreActive || moreOpen, moreBadge, () => setMoreOpen(true))}
      </Box>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '80vh',
            pb: 'calc(8px + env(safe-area-inset-bottom))',
          },
        }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1.5, mb: 0.5 }} />
        <List aria-label="More destinations" sx={{ px: 1 }}>
          {overflow.map((hub) => {
            const items = visibleItems(hub, navPerms);
            const hubActive = activeHub?.id === hub.id;
            const badge = badgeFor(hub);
            const icon = (
              <ListItemIcon sx={{ color: hubActive ? primaryColor : 'text.secondary', minWidth: 44 }}>
                <Badge badgeContent={badge} max={99} color="error">{HUB_ICONS[hub.icon]}</Badge>
              </ListItemIcon>
            );
            if (items.length === 1) {
              return (
                <ListItemButton key={hub.id} selected={hubActive} onClick={() => go(items[0].path)} sx={{ borderRadius: 3, minHeight: 52 }}>
                  {icon}
                  <ListItemText primary={hub.label} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              );
            }
            const open = expanded !== null ? expanded === hub.id : hubActive;
            return (
              <React.Fragment key={hub.id}>
                <ListItemButton onClick={() => setExpanded(open ? '' : hub.id)} sx={{ borderRadius: 3, minHeight: 52 }} aria-expanded={open}>
                  {icon}
                  <ListItemText primary={hub.label} primaryTypographyProps={{ fontWeight: 600 }} />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pl: 7 }}>
                    {items.map((it) => {
                      const itActive = location.pathname === it.path || location.pathname.startsWith(it.path + '/');
                      return (
                        <ListItemButton key={it.path} selected={itActive} onClick={() => go(it.path)} sx={{ borderRadius: 3, minHeight: 48 }}>
                          <ListItemText primary={it.label} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
          {adminLinks.length > 0 && (
            <>
              {overflow.length > 0 && <Divider sx={{ my: 1 }} />}
              {adminLinks.map((l) => {
                const active = location.pathname === l.path || location.pathname.startsWith(l.path + '/');
                return (
                  <ListItemButton key={l.path} selected={active} onClick={() => go(l.path)} sx={{ borderRadius: 3, minHeight: 52 }}>
                    <ListItemIcon sx={{ color: active ? primaryColor : 'text.secondary', minWidth: 44 }}>{l.icon}</ListItemIcon>
                    <ListItemText primary={l.label} primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItemButton>
                );
              })}
            </>
          )}
        </List>
      </Drawer>
    </>
  );
}
